import { DatePipe, DecimalPipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostListener,
  Output,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  signal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, MatSortHeader, Sort } from '@angular/material/sort';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource,
} from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PayeeIconButtonComponent } from 'src/app/shared/payee-buttons/payee-icon-button/payee-icon-button.component';
import { PayeeCasesService } from 'src/app/shared/payee-cases/payee-cases.service';
import {
  Pagination,
  UNKNOWN_CLIENT_NAME,
} from 'src/app/shared/payee-cases/types';
import { PayeeIconComponent } from 'src/app/shared/payee-icon/payee-icon.component';
import { environment } from 'src/environments/environment';
import { EventsService } from '../events.service';
import { EventFile, EventTableItem } from '../types';

@Component({
  selector: 'app-events-table',
  templateUrl: './events-table.component.html',
  styleUrls: ['./events-table.component.scss'],
  providers: [MatTable, DatePipe],
  imports: [
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    PayeeIconComponent,
    PayeeIconButtonComponent,
    MatTooltip,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatPaginator,
    DatePipe,
    DecimalPipe,
    TranslateModule,
  ],
})
export class EventsTableComponent {
  private snackbar = inject(MatSnackBar);
  private router = inject(Router);
  private translate = inject(TranslateService);
  private eventsService = inject(EventsService);
  private payeeCasesService = inject(PayeeCasesService);

  readonly showClientColumn = input(true);
  private readonly clientFilter = toSignal(this.payeeCasesService.client);

  private readonly desktopColumnsWithoutClient = [
    'payee_case_reference_id',
    'debtor_name',
    'happened_at',
    'label',
    'to_state',
    'actions',
  ];
  private readonly mobileColumns = [
    'payee_case_reference_id',
    'debtor_name',
    'label',
    'actions',
  ];

  readonly dataSource = input.required<MatTableDataSource<EventTableItem>>();
  readonly pagination = input<Pagination>();

  @Output() page = new EventEmitter<PageEvent>();

  private readonly isMobile = signal(
    typeof window !== 'undefined' &&
    (window.innerWidth <= 768 || screen.width <= 768),
  );

  readonly displayedColumns = computed(() => {
    if (this.isMobile()) {
      return this.mobileColumns;
    }

    if (!this.showClientColumn() || this.clientFilter()) {
      return this.desktopColumnsWithoutClient;
    }

    const columns = [...this.desktopColumnsWithoutClient];
    columns.splice(2, 0, 'client');
    return columns;
  });

  expandedElement: EventTableItem | null = null;

  constructor() {
    effect(() => {
      this.expandedElement = null;
      if (this.dataSource().data.length === 1) {
        this.expandedElement = this.dataSource().data[0];
      }
    });
  }

  @HostListener('window:resize') setIsMobile(): void {
    this.isMobile.set(window.innerWidth <= 768 || screen.width <= 768);
  }

  @ViewChild(MatSort) set sort(sort: MatSort) {
    const sortBy = this.eventsService.sort_by.value;
    if (sortBy && sort.active !== sortBy) {
      setTimeout(() => {
        sort.sort({
          disableClear: false,
          id: sortBy,
          start: this.eventsService.sort_direction.value,
        });
      });
    }
  }

  onSortChange(sort: Sort): void {
    this.eventsService.sort_by.next(sort.direction ? sort.active : null);
    this.eventsService.sort_direction.next(sort.direction);
  }

  updateExpandedElement(element: EventTableItem): void {
    this.expandedElement = this.expandedElement === element ? null : element;
  }

  async copyCaseIdToClipboard(
    caseId: string,
    event: MouseEvent,
  ): Promise<void> {
    event.stopPropagation();

    try {
      await navigator.clipboard.writeText(caseId);
      this.snackbar.open(
        this.translate.instant('EVENTS.COPY_SUCCESS'),
        this.translate.instant('OK'),
        { duration: 2000 },
      );
    } catch (error) {
      console.error('Failed to copy to clipboard', error);
    }
  }

  goToCase(element: EventTableItem, event: MouseEvent): void {
    event.stopPropagation();
    void this.router.navigate(['/user/cases'], {
      queryParams: {
        payee_case_reference_id: element.payee_case_reference_id,
      },
    });
  }

  filterByClient(element: EventTableItem, event: MouseEvent): void {
    event.stopPropagation();

    if (!element.client_id) {
      return;
    }

    this.payeeCasesService.client.next({
      uuid: element.client_id,
      name: element.client_name || UNKNOWN_CLIENT_NAME,
    });
  }

  downloadUrlOfFile(file: EventFile): string {
    return this.downloadUrlOfMediaUuid(file.id);
  }

  downloadUrlOfMediaUuid(mediaUuid: string): string {
    return `${environment.baseUrl}/api/file/${mediaUuid}`;
  }

  getEventLabelMediaUuid(element: EventTableItem): string | null {
    if (element.notifications.length !== 1) {
      return null;
    }

    return element.notifications[0]?.media_uuid ?? null;
  }

  shouldLinkEventLabel(element: EventTableItem): boolean {
    return !!this.getEventLabelMediaUuid(element);
  }

  getRoutesTooltip(routes: string[]): string | null {
    const uniqueRoutes = [
      ...new Set(routes.map((route) => route.trim()).filter(Boolean)),
    ];

    if (uniqueRoutes.length === 0) {
      return null;
    }

    return uniqueRoutes.map((route) => `• ${route}`).join('\n');
  }

  getEventLabelNotificationRoutesTooltip(
    element: EventTableItem,
  ): string | null {
    if (element.notifications.length !== 1) {
      return null;
    }

    return this.getRoutesTooltip(element.notifications[0]?.routes ?? []);
  }
}
